import { useRef, useEffect, useState } from 'react'

interface OTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  error?: boolean
  className?: string
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  className = ''
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Sync external value with internal state
  useEffect(() => {
    if (value && value.length > 0) {
      const newOtp = value.split('').slice(0, length)
      const paddedOtp = [...newOtp, ...Array(length - newOtp.length).fill('')]
      setOtp(paddedOtp)
    } else if (value === '') {
      setOtp(Array(length).fill(''))
    }
  }, [value, length])

  // Auto-focus first field on mount
  useEffect(() => {
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0]?.focus()
    }
  }, [disabled])

  const handleChange = (index: number, newValue: string) => {
    // Only keep digits
    const digit = newValue.replace(/\D/g, '').slice(0, 1)
    
    if (digit === '' && newValue === '') {
      // Delete - clear current field
      const newOtp = [...otp]
      newOtp[index] = ''
      setOtp(newOtp)
      onChange(newOtp.join(''))
      
      // Focus previous field if empty
      if (index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    } else if (digit) {
      // Enter digit
      const newOtp = [...otp]
      newOtp[index] = digit
      setOtp(newOtp)
      
      const otpValue = newOtp.join('')
      onChange(otpValue)
      
      // Focus next field if available
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      } else if (otpValue.length === length && onComplete) {
        onComplete(otpValue)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace - delete and go to previous field
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    // Arrow left - previous field
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    // Arrow right - next field
    else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    
    if (pastedData) {
      const newOtp = [...otp]
      for (let i = 0; i < pastedData.length && i < length; i++) {
        newOtp[i] = pastedData[i]
      }
      setOtp(newOtp)
      
      const otpValue = newOtp.join('')
      onChange(otpValue)
      
      // Focus last filled field or next
      const nextIndex = Math.min(pastedData.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
      
      if (otpValue.length === length && onComplete) {
        onComplete(otpValue)
      }
    }
  }

  return (
    <div className={`flex justify-center gap-2 ${className}`}>
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={`
            w-14 h-16 text-center text-2xl font-bold
            border-2 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2
            transition-colors
            ${error 
              ? 'border-red-500 focus:ring-red-500 bg-red-50' 
              : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
            }
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          `}
          style={{
            caretColor: error ? '#ef4444' : '#E11D48'
          }}
        />
      ))}
    </div>
  )
}
